import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './ContentCard.css';

const ContentCard = ({ content, isPurchased = false }) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggleButton, setShowToggleButton] = useState(false);
  const descriptionRef = useRef(null);

  // Используем поля из MongoDB структуры, заменяем previewText на description
  const { 
    _id, 
    title, 
    description, // Используем description вместо previewText
    imageUrl, 
    price, 
    oldPrice, 
    isFree, 
    isPinned,
    hasPreview,
    isAnnouncement,
    tags = []
  } = content;

  useEffect(() => {
    if (descriptionRef.current) {
      // Проверяем, превышает ли фактическая высота контента высоту трех строк (примерно 60px, можно настроить)
      // Или если текст достаточно длинный
      const lineHeight = parseInt(window.getComputedStyle(descriptionRef.current).lineHeight, 10) || 20;
      const maxHeight = lineHeight * 3;
      if (descriptionRef.current.scrollHeight > maxHeight || (description && description.length > 150)) { // 150 символов как примерный порог
        setShowToggleButton(true);
      } else {
        setShowToggleButton(false);
        setIsExpanded(true); // Если текст короткий, сразу показываем полностью
      }
    }
  }, [description]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`content-card ${isPinned ? 'pinned' : ''}`}>
      {isPinned && <div className="pinned-badge">Закреплено</div>}
      
      {imageUrl && (
        <div className="card-image">
          <img src={imageUrl} alt={title} />
          {isFree && <div className="free-badge">Бесплатно</div>}
        </div>
      )}
      
      <div className="card-content">
        {tags.length > 0 && (
          <div className="card-tags">
            {tags.map(tag => (
              <span key={tag._id} className="tag">
                {tag.name}
              </span>
            ))}
          </div>
        )}
        <h3 className="card-title">{title}</h3>
        
        {description && (
          <div className="card-description-wrapper">
            <div
              ref={descriptionRef}
              className={`card-description ${isExpanded ? 'expanded' : 'collapsed'}`}
              dangerouslySetInnerHTML={{ __html: description }}
            />
            {showToggleButton && (
              <button onClick={toggleExpand} className="expand-toggle-button">
                {isExpanded ? 'Свернуть описание ↑' : 'Развернуть описание ↓'}
              </button>
            )}
          </div>
        )}
        
        <div className="card-footer">
        <div className="price-block">
          {isAnnouncement ? null : isPurchased ? (
            <span className="purchased-badge">Оплачено</span>
          ) : isFree ? (
            <span className="free-price">Бесплатно</span>
          ) : (
            <>
              <span className="current-price">{price} ₽</span>
              {oldPrice && <span className="old-price">{oldPrice} ₽</span>}
            </>
          )}
        </div>
          
          <div className="button-container">
            {isAnnouncement ? (
              <div className="announcement-text">Скоро</div>
            ) : (
              <>
                {hasPreview && !isPurchased && ( // Добавляем проверку !isPurchased
                  <button 
                    onClick={() => {
                      try {
                        // Прямое открытие бота через Telegram API с командой start=preview_id
                        if (window.Telegram && window.Telegram.WebApp) {
                          // Получаем имя бота из переменной окружения
                          const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
                          if (!botUsername) {
                            console.error("Переменная REACT_APP_TELEGRAM_BOT_USERNAME не установлена в .env файле фронтенда.");
                            alert("Ошибка конфигурации: не указано имя пользователя бота. Обратитесь к администратору.");
                            return;
                          }
                          
                          // Используем специальный параметр preview_materials_ для получения превью
                          console.log('Opening preview link to bot...');
                          window.Telegram.WebApp.openTelegramLink(`https://t.me/${botUsername}?start=preview_materials_${_id}`);
                          
                          // Отложенное закрытие WebApp после открытия бота
                          setTimeout(() => {
                            window.Telegram.WebApp.close();
                          }, 100);
                        }
                      } catch (error) {
                        console.error('Ошибка при открытии бота для превью:', error);
                        alert('Не удалось открыть превью. Пожалуйста, попробуйте позже.');
                      }
                    }}
                    className="preview-button"
                  >
                    Превью
                  </button>
                )}
                
                {isFree || isPurchased ? (
                  <button 
                    onClick={() => {
                      try {
                        setLoading(true);
                        
                        // Прямое открытие бота через Telegram API с командой start=view_id
                        if (window.Telegram && window.Telegram.WebApp) {
                          // Получаем имя бота из переменной окружения
                          const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
                           if (!botUsername) {
                            console.error("Переменная REACT_APP_TELEGRAM_BOT_USERNAME не установлена в .env файле фронтенда.");
                            alert("Ошибка конфигурации: не указано имя пользователя бота. Обратитесь к администратору.");
                            setLoading(false); // Сбрасываем состояние загрузки
                            return;
                          }
                          
                          // Использование корректного формата ссылки для перехода к боту
                          console.log('Opening link to bot...');
                          window.Telegram.WebApp.openTelegramLink(`https://t.me/${botUsername}?start=view_${_id}`);
                          
                          // Отложенное закрытие WebApp после открытия бота
                          setTimeout(() => {
                            window.Telegram.WebApp.close();
                          }, 100);
                        }
                      } catch (error) {
                        console.error('Ошибка при открытии бота:', error);
                        alert('Не удалось получить материалы. Пожалуйста, попробуйте позже.');
                        setLoading(false);
                      }
                    }}
                    className="view-button"
                    disabled={loading}
                  >
                    {loading ? "Отправка..." : "Получить"}
                  </button>
                ) : (
                  <button 
                    onClick={async () => {
                      try {
                        setLoading(true);
                        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
                        
                        console.log('Отправка запроса на создание платежа:', {
                          telegramId: telegramId,
                          contentId: _id
                        });
                        
                        // Преобразуем telegramId в число, если это возможно
                        let tgId = telegramId;
                        if (telegramId && !isNaN(telegramId)) {
                          tgId = parseInt(telegramId, 10);
                        }
                        
                        // Добавляем обработку ошибки 403 Forbidden
                        const response = await fetch('/api/payments/create', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'X-Telegram-ID': tgId
                          },
                          body: JSON.stringify({
                            contentId: _id,
                            returnUrl: window.location.origin
                          }),
                        });
                        
                        if (!response.ok) {
                          // Специальная обработка ошибки авторизации
                          if (response.status === 403) {
                            throw new Error('Ошибка авторизации. Пожалуйста, перезапустите бота.');
                          }
                          // Читаем ответ сервера для получения деталей ошибки
                          const errorData = await response.json();
                          throw new Error(errorData.error || `Ошибка HTTP: ${response.status} ${response.statusText}`);
                        }
                        
                        const data = await response.json();
                        console.log('Ответ сервера:', data);
                        
                        if (data.success && data.payment?.paymentUrl) {
                          // Открываем ссылку оплаты во внешнем браузере, а не в WebApp
                          if (window.Telegram && window.Telegram.WebApp) {
                            window.Telegram.WebApp.openLink(data.payment.paymentUrl);
                          } else {
                            // Резервный вариант, если Telegram WebApp недоступен
                            window.open(data.payment.paymentUrl, '_blank');
                          }
                        } else {
                          alert(data.error || 'Ошибка при создании платежа');
                        }
                      } catch (error) {
                        console.error('Ошибка при создании платежа:', error);
                        alert(`Не удалось создать платёж: ${error.message}`);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="view-button"
                    disabled={loading}
                  >
                    {loading ? "Загрузка..." : "Купить"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentCard;
